/**
 * Consumer Submit Claim Page - 2-step wizard
 *
 * Step 1: Claim details form (schema-driven fields from metadata)
 * Step 2: Review + submit
 *
 * The holding UUID comes from the route param - no holding selection step needed.
 *
 * Uses generated `useForm` metadata fields for all dynamic claim inputs.
 * The old inline ClaimField / ConsumerImageUpload components have been replaced.
 */

import { useReducer, useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { useConsumerHolding } from '@/consumer/hooks/use-holdings'
import {
  useConsumerClaimMetadata,
  useConsumerCreateClaim,
} from '@/consumer/hooks/use-claims'
import { useConsumerFileUpload } from '@/consumer/hooks/use-registration'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  useForm,
  normaliseClaimField,
  formatReviewValueNode as sharedFormatReviewValue,
} from '@/shared/lib/forms'
import { buildFormFieldFromMeta } from '@/shared/lib/forms/generated-metadata'
import { useFormattingContext } from '@/shared/lib/formatting'
import { DisclaimerAck } from '@/shared/components/claims/disclaimer-ack'
import { SmsConsentAck } from '@/shared/components/claims/sms-consent-ack'
import {
  disclaimerAckValue,
  disclaimerBlocks,
  type DisclaimerConfig,
} from '@/shared/components/claims/disclaimer'
import { type SmsConsentMeta } from '@/consumer/hooks/use-claims'

// ── State ────────────────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'
const STEPS = ['Claim Details', 'Review & Submit']

interface WizardState {
  step: number
}

const initialState: WizardState = {
  step: 0,
}

type Action =
  | { type: 'NEXT' }
  | { type: 'BACK' }

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case 'NEXT':
      return { ...state, step: Math.min(state.step + 1, STEPS.length - 1) }
    case 'BACK':
      return { ...state, step: Math.max(state.step - 1, 0) }
    default:
      return state
  }
}

// ── Component ────────────────────────────────────────────

export default function SubmitClaimPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [reviewValues, setReviewValues] = useState<Record<string, unknown>>({})
  // Rich upload envelopes ({ code_name -> { uuid, file_name, file_url } })
  // captured at submit time so the review step can render file/photo fields
  // as filename links. The submission payload still carries the bare UUID.
  const [reviewUploads, setReviewUploads] = useState<Record<string, unknown>>(
    {},
  )

  const next = useCallback(() => dispatch({ type: 'NEXT' }), [])
  const back = useCallback(() => dispatch({ type: 'BACK' }), [])

  const { data: holding, isLoading: holdingLoading } = useConsumerHolding(uuid)
  const { data: metadata, isLoading: metadataLoading } = useConsumerClaimMetadata(uuid)
  const createClaim = useConsumerCreateClaim()
  const uploadMutation = useConsumerFileUpload()

  // Tenant-authored cut-hose disclaimer, shown once at Review & Submit and
  // gating submission when acknowledgement is required (DL-44). Config-driven:
  // tenants that author no `claim_submit` disclaimer render nothing.
  const submitDisclaimer: DisclaimerConfig | null =
    metadata?.disclaimers?.claim_submit ?? null
  const [submitAck, setSubmitAck] = useState(false)

  // SMS opt-in, shown at Review & Submit only when the metadata carried an
  // `sms_consent` block (backend-gated on the tenant's `sms` flag). Seeded once
  // from the consumer's current consent so the box reflects prior opt-in,
  // without clobbering a toggle the consumer makes afterwards.
  const smsConsent: SmsConsentMeta | null = metadata?.sms_consent ?? null
  const [smsOptIn, setSmsOptIn] = useState(false)
  const smsSeeded = useRef(false)
  // Route param changes reuse this component (no unmount), so per-holding
  // consent state would otherwise leak between holdings. Reset it when the
  // holding uuid changes; the seed effect below then re-seeds from the new
  // holding's metadata.
  useEffect(() => {
    setSubmitAck(false)
    setSmsOptIn(false)
    smsSeeded.current = false
  }, [uuid])
  useEffect(() => {
    if (smsConsent && !smsSeeded.current) {
      setSmsOptIn(smsConsent.opted_in)
      smsSeeded.current = true
    }
  }, [smsConsent])

  // Normalise backend fields
  const normalisedFields: FormFieldMeta[] = useMemo(
    () => (metadata?.fields ?? []).map(normaliseClaimField),
    [metadata?.fields],
  )
  const generatedFields = useMemo(
    () =>
      normalisedFields.map((field) =>
        buildFormFieldFromMeta(field, metadata?.prefill?.[field.code_name]),
      ),
    [normalisedFields, metadata?.prefill],
  )
  const { Form: ClaimDetailsForm, ref: claimFormRef } = useForm(
    {
      name: 'consumer-claim-form',
      submit: () => null,
      fields: generatedFields,
      uploadFile: ({ file, use_case }) => uploadMutation.mutateAsync({ file, use_case }),
    },
    async (values, _ctx, uploads) => {
      setReviewValues(values)
      setReviewUploads(uploads ?? {})
      next()
    },
    [uuid, normalisedFields],
  )

  const [successData, setSuccessData] = useState<{
    claimUuid: string
    reference: string
  } | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!uuid) return

    try {
      const captured = { ...(claimFormRef.current?.values ?? {}) }
      // Record the acknowledgement as data on the claim when ticked.
      if (submitDisclaimer && submitAck) {
        captured.disclaimer_acknowledged = disclaimerAckValue(submitDisclaimer)
      }
      const result = await createClaim.mutateAsync({
        holding_uuid: uuid,
        captured_values: captured,
        // Only send when the tenant's `sms` flag surfaced the checkbox.
        ...(smsConsent ? { sms_opt_in: smsOptIn } : {}),
      })
      const ref = `CL-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`
      setSuccessData({ claimUuid: result.uuid, reference: ref })
    } catch (err: unknown) {
      toastApiError(err, 'Failed to submit claim');
    }
  }, [uuid, claimFormRef, createClaim, submitDisclaimer, submitAck, smsConsent, smsOptIn])

  if (holdingLoading || metadataLoading) {
    return (
      <ConsumerShell>
        <div className="flex items-center justify-center py-20">
          <p className="text-[13px] text-[var(--ink-3)]">Loading...</p>
        </div>
      </ConsumerShell>
    );
  }

  if (!holding) {
    return (
      <ConsumerShell>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-[13px] text-[var(--ink-3)]">Product not found</p>
          <Button
            variant="link"
            onClick={() => navigate('/')}
            className="text-[11px] underline cursor-pointer text-[var(--green)]"
          >
            Back to home
          </Button>
        </div>
      </ConsumerShell>
    );
  }

  const stepContent = (() => {
    switch (state.step) {
      case 0:
        return (
          <StepClaimDetails
            Form={ClaimDetailsForm}
            generatedFields={generatedFields}
            back={() => navigate(`/products/${uuid}`)}
            uploading={uploadMutation.isPending}
          />
        )
      case 1:
        return (
          <StepReview
            fields={normalisedFields}
            values={reviewValues}
            uploads={reviewUploads}
            back={back}
            onSubmit={handleSubmit}
            isSubmitting={createClaim.isPending}
            disclaimer={submitDisclaimer}
            acknowledged={submitAck}
            onAcknowledgedChange={setSubmitAck}
            smsConsent={smsConsent}
            smsOptIn={smsOptIn}
            onSmsOptInChange={setSmsOptIn}
          />
        )
      default:
        return null
    }
  })()

  // Claim submitted - show confirmation
  if (successData) {
    return (
      <ConsumerShell>
        <div className="mx-auto max-w-[780px] py-[60px] text-center">
          {/* Shield icon */}
          <div className="mb-4">
            <ShieldCheck className="mx-auto h-8 w-8 text-[var(--green)]" />
          </div>

          <h2 className="mb-3 font-display text-[48px] tracking-[-0.01em] text-[var(--ink)]">
            {successData.reference}
          </h2>

          <div className="mx-auto mb-5 h-[0.5px] w-12 bg-[var(--rule-strong)]" />

          <p className="mb-8 text-[13px] leading-[1.7] tracking-[0.04em] text-[var(--ink-3)]">
            Claim submitted. A confirmation email<br />
            has been sent to you automatically.
          </p>

          <p className="mb-8 text-[12px] text-[var(--ink-3)]">
            {holding.product.name}
          </p>

          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/claims/${successData.claimUuid}`)}
              className="h-9 cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent px-5 text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink)] hover:border-[var(--ink-3)]"
            >
              View Claim
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/products/${uuid}`)}
              className="h-9 cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent px-5 text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink)] hover:border-[var(--ink-3)]"
            >
              Back to Product
            </Button>
          </div>
        </div>
      </ConsumerShell>
    );
  }

  return (
    <ConsumerShell>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate(`/products/${uuid}`)}
        className="mb-6 flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] cursor-pointer hover:bg-[var(--bg-subtle)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </Button>
      {/* Header */}
      <div className="mb-6">
        <h1 className="mb-2 text-[22px] text-[var(--ink)]">Submit a Claim</h1>
        <p className="text-[13px] text-[var(--ink-3)]">
          {holding.product.name}
        </p>
      </div>
      {/* Stepper */}
      <div className="mb-6 flex items-center">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center">
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-normal',
                  i <= state.step
                    ? 'bg-[var(--green)] text-white'
                    : 'border-[0.5px] border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]',
                )}
              >
                {i < state.step ? '\u2713' : i + 1}
              </span>
              <span
                className={cn(
                  "whitespace-nowrap text-[10px] uppercase tracking-[0.12em]",
                  i <= state.step ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]',
                )}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="mx-2 h-[0.5px] w-10 shrink-0 bg-[var(--rule-strong)]" />
            )}
          </div>
        ))}
      </div>
      {stepContent}
    </ConsumerShell>
  );
}

// ── Step 1: Claim Details ────────────────────────────────

function StepClaimDetails({
  Form,
  generatedFields,
  back,
  uploading,
}: {
  Form: ReturnType<typeof useForm>['Form']
  generatedFields: ReturnType<typeof buildFormFieldFromMeta>[]
  back: () => void
  uploading: boolean
}) {
  if (generatedFields.length === 0) {
    return (
      <div>
        <p className="py-5 text-[13px] text-[var(--ink-3)]">
          No additional details required for this claim type.
        </p>
        <div className="mt-6 flex justify-between">
          <Button onClick={back} variant="outline" className={ghostBtnClass}>
            Back
          </Button>
          <Button onClick={next} variant="default" className={greenBtnClass}>
            Next
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {() => (
        <div className="col-span-full mt-6 flex justify-between">
          <Button onClick={back} type="button" variant="outline" className={ghostBtnClass}>
            Back
          </Button>
          <Button type="submit" disabled={uploading} variant="default" className={greenBtnClass}>
            Next
          </Button>
        </div>
      )}
    </Form>
  )
}

// ── Step 2: Review & Submit ──────────────────────────────

function StepReview({
  fields,
  values,
  uploads,
  back,
  onSubmit,
  isSubmitting,
  disclaimer,
  acknowledged,
  onAcknowledgedChange,
  smsConsent,
  smsOptIn,
  onSmsOptInChange,
}: {
  fields: FormFieldMeta[]
  values: Record<string, unknown>
  uploads: Record<string, unknown>
  back: () => void
  onSubmit: () => void
  isSubmitting: boolean
  disclaimer: DisclaimerConfig | null
  acknowledged: boolean
  onAcknowledgedChange: (value: boolean) => void
  smsConsent: SmsConsentMeta | null
  smsOptIn: boolean
  onSmsOptInChange: (value: boolean) => void
}) {
  const { locale, currency } = useFormattingContext()
  return (
    <div>
      <p className="mb-5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        Review & Submit
      </p>
      {fields.length > 0 && (
        <div className="mb-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-[14px]">
          <p className="mb-2.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Claim Details
          </p>
          {fields.map((field) => {
            const value = values[field.code_name]
            return (
              <div
                key={field.code_name}
                className="flex items-baseline justify-between border-b-[0.5px] border-[var(--rule)] py-1.5 last:border-b-0"
              >
                <span className="text-[12px] text-[var(--ink-3)]">
                  {field.display_name}
                </span>
                <span className="text-[13px] text-[var(--ink)]">
                  {sharedFormatReviewValue(field, value, uploads, {
                    locale,
                    currency,
                  })}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {disclaimer && (
        <DisclaimerAck
          className="mb-4"
          disclaimer={disclaimer}
          acknowledged={acknowledged}
          onAcknowledgedChange={onAcknowledgedChange}
        />
      )}
      {smsConsent && (
        <SmsConsentAck
          className="mb-4"
          disclosure={smsConsent.disclosure}
          checked={smsOptIn}
          onCheckedChange={onSmsOptInChange}
        />
      )}
      <div className="mt-6 flex justify-between">
        <Button onClick={back} disabled={isSubmitting} variant="outline" className={ghostBtnClass}>
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || disclaimerBlocks(disclaimer, acknowledged)}
          variant="default"
          className={cn(greenBtnClass, isSubmitting && 'opacity-70')}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Claim'}
        </Button>
      </div>
    </div>
  );
}

// ── Button class names ──

const greenBtnClass =
  'cursor-pointer rounded-[6px] bg-[var(--green)] px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em] text-white'

const ghostBtnClass =
  'cursor-pointer rounded-[6px] border border-input bg-transparent px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em] text-[var(--ink-2)]'
